import { useEffect, useState, useCallback } from "react";
import AppNav from "../components/AppNav";
import UploadPanel from "../components/UploadPanel";
import SubtitlePreview from "../components/SubtitlePreview";
import EnginePipeline from "../components/EnginePipeline";
import HistorySidebar from "../components/HistorySidebar";
import FeedbackWall from "../components/FeedbackWall";
import ConsentModal from "../components/ConsentModal";
import LegalFooter from "../components/LegalFooter";
import UsageBar from "../components/UsageBar";
import VideoEditor from "../components/VideoEditor";
import ReferralCard from "../components/ReferralCard";
import ReferralBanner from "../components/ReferralBanner";
import WelcomeModal from "../components/WelcomeModal";
import { api, API } from "../lib/api";
import { uploadFileToR2 } from "../lib/r2Upload";
import { pollJobUntilDone } from "../lib/jobPoll";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Dashboard() {
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [phase, setPhase] = useState("");
  const [consentNeeded, setConsentNeeded] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/subtitles/history");
      setHistory(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Load history failed:", err?.message);
    }
  }, []);

  const checkConsent = useCallback(async () => {
    try {
      const { data } = await api.get("/legal/consent");
      setConsentNeeded(!data.accepted);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Load consent failed:", err?.message);
    }
  }, []);

  useEffect(() => { loadHistory(); checkConsent(); }, [loadHistory, checkConsent]);

  const handleGenerate = async ({ input_type, text_input, media_url, file, source_language, target_language, narrator_gender, subtitle_text, subtitle_format }) => {
    setProcessing(true);
    setJob(null);
    try {
      const form = new FormData();
      form.append("input_type", input_type);
      form.append("source_language", source_language);
      form.append("target_language", target_language);
      form.append("narrator_gender", narrator_gender);
      if (input_type === "text") {
        setPhase("Translating...");
        form.append("text_input", text_input);
      } else if (["youtube", "instagram", "tiktok"].includes(input_type)) {
        const label = { youtube: "YouTube", instagram: "Reel", tiktok: "TikTok" }[input_type];
        setPhase(`Fetching ${label} audio...`);
        form.append("media_url", media_url);
      } else {
        // Direct browser → Cloudflare R2 multipart upload — the file never
        // touches our backend, so we're not bound by the 40 MB ingress cap.
        setPhase("Uploading 0%…");
        const { r2_key } = await uploadFileToR2(file, (pct) => {
          setPhase(`Uploading ${pct}%…`);
        });
        setPhase("Queuing job...");
        form.append("r2_key", r2_key);
        if (file?.name) form.append("original_filename", file.name);
        if (subtitle_text) form.append("subtitle_text", subtitle_text);
        if (subtitle_format) form.append("subtitle_format", subtitle_format);
      }

      // Kick off the pipeline — this returns in <200 ms with just
      // {job_id, status:"processing"}. The actual transcription + engine
      // run in the background so the request never bumps into Cloudflare's
      // 100 s edge timeout.
      const { data: kickoff } = await api.post("/subtitles/generate", form, { timeout: 30000 });
      if (!kickoff.job_id) throw new Error("Job did not start");

      // Poll until the pipeline finishes. Progress messages come from the
      // doc's `phase` (+ `progress.chunks_done` on long transcriptions) so
      // the user sees live movement on a 40-minute video instead of a
      // silent spinner. See lib/jobPoll.js for the cadence + resilience.
      const cur = await pollJobUntilDone(kickoff.job_id, (label) => setPhase(label));
      if (cur._stillRunning) {
        loadHistory();
        toast.info(
          "Still processing — this keeps running on our side. You can close this " +
          "page; the finished subtitles appear in your History."
        );
        return;
      }
      if (cur.status === "done" || cur.status === "done_with_warnings") {
        setJob(cur);
        if (cur.status === "done_with_warnings") {
          const p0 = cur?.qa_report?.p0_failures || [];
          toast.warning(
            p0.length
              ? `Subtitles generated with QA warnings: ${p0.join(", ")}`
              : "Subtitles generated with QA warnings — review before download."
          );
        } else {
          toast.success("Subtitles generated");
        }
        loadHistory();
        return;
      }
      if (cur.status === "failed") {
        // Keep the failed doc in state so the UI can offer a Retry.
        setJob(cur);
        loadHistory();
        toast.error(cur.error || "Job failed — you can retry without re-uploading.");
        return;
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
      const msg = e?.response?.data?.detail || e?.message || "Generation failed";
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally {
      setProcessing(false);
      setPhase("");
    }
  };

  // Kicks the failed pipeline off again without asking the user to re-upload.
  const handleRetry = async (jobId) => {
    if (!jobId) return;
    setProcessing(true);
    setPhase("Retrying…");
    try {
      await api.post(`/subtitles/${jobId}/retry`);
      const cur = await pollJobUntilDone(jobId, (label) => setPhase(label));
      if (cur._stillRunning) {
        loadHistory();
        toast.info("Still processing — it keeps running even if you leave this page.");
        return;
      }
      if (cur.status === "done" || cur.status === "done_with_warnings") {
        setJob(cur);
        loadHistory();
        if (cur.status === "done_with_warnings") {
          const p0 = cur?.qa_report?.p0_failures || [];
          toast.warning(
            p0.length
              ? `Retry finished with QA warnings: ${p0.join(", ")}`
              : "Retry finished with QA warnings — review before download."
          );
        } else {
          toast.success("Retry succeeded");
        }
        return;
      }
      if (cur.status === "failed") {
        setJob(cur); loadHistory(); toast.error(cur.error || "Retry failed"); return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Retry failed");
    } finally {
      setProcessing(false); setPhase("");
    }
  };

  const handleDownload = async (fmt) => {
    if (!job) return;
    try {
      const url = `${API}/subtitles/${job.job_id}/download/${fmt}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `${job.job_id}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const handleHistoryOpen = async (id) => {
    try {
      const { data } = await api.get(`/subtitles/${id}`);
      setJob(data);
    } catch { toast.error("Could not open job"); }
  };

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <AppNav />
      <main className="pt-24 pb-16 px-4 md:px-8 max-w-7xl mx-auto relative z-10">
        <div className="mb-10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Subtitle Studio · Personal translation · Whisper + GPT-5.4
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            Transcribe, translate, ship.
          </h1>
        </div>

        <div className="mb-6">
          <UsageBar refreshKey={job?.job_id || 0} />
        </div>

        {/* Compact top-of-fold referral nudge — auto-hides for paid users
            without a pending discount. Full ReferralCard is further down. */}
        <div className="mb-6">
          <ReferralBanner />
        </div>

        {/* Post-signup welcome modal — fires once per new user via
            localStorage flag set by Login.jsx. */}
        <WelcomeModal />

        {processing && (
          <div data-testid="processing-status-indicator" className="mb-6 border border-amber-400/20 bg-amber-400/5 p-4 space-y-2">
            <div className="flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
              <div className="text-sm text-amber-200 font-mono uppercase tracking-widest">{phase || "Working..."}</div>
              <div className="ml-auto text-[10px] font-mono text-zinc-500">audio/video jobs can take several minutes</div>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 pl-7" data-testid="processing-safe-to-leave">
              Long videos keep processing on our side — you can close this page and
              pick the result up from History.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <UploadPanel onGenerate={handleGenerate} processing={processing} />
            {job?.status === "failed" && (
              <div
                data-testid="job-failed-banner"
                className="border border-red-500/30 bg-red-500/10 p-4 space-y-3"
              >
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-red-400">
                  {job.failure_reason === "timeout" ? "Job timed out" : "Job failed"}
                </div>
                <div className="text-sm text-red-100 leading-relaxed" data-testid="job-failed-message">
                  {job.failure_reason === "timeout"
                    ? "This job was still processing after 180 minutes and was stopped as timed out. Nothing was lost — your upload, languages and options are saved, so you can re-submit it as-is."
                    : (job.error || "The pipeline errored out. You can retry — we still have your upload.")}
                </div>
                <button
                  type="button"
                  data-testid="job-retry-btn"
                  onClick={() => handleRetry(job.job_id)}
                  disabled={processing}
                  className="text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-100 disabled:opacity-50 px-4 py-2"
                >
                  {job.failure_reason === "timeout" ? "Retry with the same settings" : "Retry without re-uploading"}
                </button>
              </div>
            )}
            {job?.status === "done_with_warnings" && (
              <div
                data-testid="job-qa-warning-banner"
                className="border border-amber-400/40 bg-amber-400/10 p-4 space-y-2"
              >
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-300">
                  QA warnings · Score {job?.qa_report?.score ?? "?"}/100
                </div>
                <div className="text-sm text-amber-100 leading-relaxed">
                  Subtitles were generated but the automated QA engine flagged
                  {" "}
                  {job?.qa_report?.p0_failures?.length || 0} P0 issue
                  {(job?.qa_report?.p0_failures?.length || 0) === 1 ? "" : "s"}.
                  Review the segments below before downloading.
                </div>
                {Array.isArray(job?.qa_report?.p0_failures) && job.qa_report.p0_failures.length > 0 && (
                  <ul
                    data-testid="job-qa-p0-list"
                    className="text-xs text-amber-200 font-mono list-disc pl-5 space-y-0.5"
                  >
                    {job.qa_report.p0_failures.map((code) => (
                      <li key={code}>{code}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {job?.pipeline_report && (
              <EnginePipeline
                report={job.pipeline_report}
                speakers={job.speakers}
              />
            )}
            <SubtitlePreview job={job} onDownload={handleDownload} />
            {job && job.has_media && (
              <VideoEditor job={job} onJobUpdated={setJob} />
            )}
            {/* Referral card — always visible on the dashboard so users
                see progress every session without hunting for a settings page. */}
            <ReferralCard />
          </div>
          <div className="lg:col-span-4">
            <HistorySidebar
              items={history}
              activeId={job?.job_id}
              onSelect={handleHistoryOpen}
            />
          </div>
        </div>

        <div className="mt-12">
          <FeedbackWall />
        </div>

        <LegalFooter />
      </main>
      <ConsentModal open={consentNeeded} onAccepted={() => setConsentNeeded(false)} />
    </div>
  );
}
